/** File upload, retry and delete for task files. */

import { del, get, patch, post, upload } from "@/services/api-client";
import type {
  FileResponse,
  FileSource,
  FileType,
  FileUploadResponse,
  FileViewUrlResponse,
  LiteratureType,
  components,
} from "@patent-studio/api-types";
import type { IntakeSlotTable } from "@/lib/intake-components";

type SupportingTag = components["schemas"]["SupportingTag"];

/**
 * Post one file, with its tag and the attorney's words about it where the slot
 * asks for them. Both are multipart fields, so a file and what it is arrive in
 * the same request and the server can refuse the pair together.
 *
 * The stage a file was chosen at is not visible to the server, so the caller
 * says. It decides whether a document is shown as having come from intake
 * and settles nothing else.
 */
export function uploadFile(
  taskId: string,
  file: File,
  fileType: FileType = "prior_art",
  description?: string,
  source: FileSource = "direct_upload",
  supportingTag?: SupportingTag,
): Promise<FileUploadResponse> {
  return upload<FileUploadResponse>(`/tasks/${taskId}/files`, file, {
    file_type: fileType,
    ...(description === undefined ? {} : { description }),
    ...(supportingTag === undefined ? {} : { supporting_tag: supportingTag }),
    source,
  });
}

/**
 * A better copy of a prior art document, stored and the old one retired under
 * one task read on the server. The reply is the new file, numbered afresh.
 */
export function replaceFile(
  taskId: string,
  fileId: string,
  replacement: File,
): Promise<FileUploadResponse> {
  return upload<FileUploadResponse>(
    `/tasks/${taskId}/files/${fileId}/replacement`,
    replacement,
  );
}

export function deleteFile(taskId: string, fileId: string): Promise<void> {
  return del(`/tasks/${taskId}/files/${fileId}`);
}

/**
 * Ask for one file to be read again.
 *
 * The response only reports what the file holds now. The outcome arrives on
 * the file-status stream, which the intake screen is already subscribed to.
 */
export function retryFileProcessing(
  taskId: string,
  fileId: string,
): Promise<FileUploadResponse> {
  return post<FileUploadResponse>(`/tasks/${taskId}/files/${fileId}/reprocess`);
}

/**
 * Record what kind of reference a prior art file is, in the attorney's word.
 * Null clears the choice.
 */
export function classifyFile(
  taskId: string,
  fileId: string,
  literatureType: LiteratureType | null,
): Promise<FileResponse> {
  return patch<FileResponse>(`/tasks/${taskId}/files/${fileId}`, {
    literature_type: literatureType,
  });
}

/** A short-lived URL to open one stored file at, asked for on demand. */
export async function fileViewUrl(
  taskId: string,
  fileId: string,
): Promise<string> {
  const { url } = await get<FileViewUrlResponse>(
    `/tasks/${taskId}/files/${fileId}/url`,
  );
  return url;
}

/** What each Intake slot takes: the table the API judges every upload by. */
export function getIntakeSlots(): Promise<IntakeSlotTable> {
  return get<IntakeSlotTable>("/intake/slots");
}
